import { motion } from "framer-motion";
import { Store, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

const palette = {
  deep: "#0b3029",
  green: "#1c9d4b",
  yellow: "#f7c744",
  cream: "#f5f7df",
  mint: "#c7ebe0",
  line: "#dbe4c8",
};

const STAGES = [
  "Preparing your workspace",
  "Loading your dashboard",
  "Almost there",
];

export function PortalLoader({ label = "Preparing your workspace" }: { label?: string }) {
  const [elapsed, setElapsed] = useState(0);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const start = Date.now();
    const timer = setInterval(() => {
      const secs = Math.floor((Date.now() - start) / 1000);
      setElapsed(secs);
      if (secs >= 12) setStuck(true);
    }, 500);
    return () => clearInterval(timer);
  }, []);

  const stageIdx = elapsed < 4 ? 0 : elapsed < 8 ? 1 : 2;
  const displayLabel = stuck ? "Taking longer than usual" : (STAGES[stageIdx] ?? label);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden" style={{ background: palette.cream }}>
      <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full opacity-20" style={{ background: palette.mint }} />
      <div className="absolute -bottom-40 -right-24 h-96 w-96 rounded-full opacity-10" style={{ background: palette.deep }} />
      <div className="relative flex flex-col items-center gap-6 px-6 text-center">
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="grid h-16 w-16 place-items-center rounded-2xl shadow-xl"
          style={{ background: palette.yellow, color: palette.deep }}
        >
          <Store className="h-7 w-7" />
        </motion.div>
        <div className="space-y-2">
          <motion.p
            key={displayLabel}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="text-sm font-extrabold tracking-[-.02em]"
            style={{ color: palette.deep }}
          >
            {displayLabel}
          </motion.p>
          <div className="flex items-center justify-center gap-1.5">
            {[0, 1, 2].map(i => (
              <motion.span
                key={i}
                animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
                className="h-2 w-2 rounded-full"
                style={{ background: palette.green }}
              />
            ))}
          </div>
        </div>
        <div className="h-1 w-44 overflow-hidden rounded-full" style={{ background: palette.line }}>
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            className="h-full w-1/2 rounded-full"
            style={{ background: palette.green }}
          />
        </div>
        {stuck && (
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition hover:opacity-80"
            style={{ borderColor: palette.green, color: palette.deep, background: "rgba(28,157,75,0.06)" }}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </motion.button>
        )}
      </div>
    </div>
  );
}
